import { css } from '@emotion/react';
import { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { darkTheme } from '@/theme/dark-theme';
import { spacing } from '@/theme/tokens';

export interface MicPermissionPromptProps {
  onGranted: () => void;
  onDenied: () => void;
}

export function MicPermissionPrompt({ onGranted, onDenied }: MicPermissionPromptProps) {
  const [error, setError] = useState(false);

  const requestMic = useCallback(async () => {
    setError(false);
    try {
      if (!navigator.mediaDevices) {
        throw new Error('mediaDevices not available');
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      onGranted();
    } catch {
      setError(true);
    }
  }, [onGranted]);

  useEffect(() => {
    requestMic();
  }, [requestMic]);

  return (
    <div
      css={css`
        position: fixed;
        inset: 0;
        z-index: 40;
        background: ${darkTheme.overlay};
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: ${spacing.lg};
      `}
    >
      {error ? (
        <>
          <p
            css={css`
              color: ${darkTheme.error};
              font-size: 18px;
              margin: 0;
            `}
          >
            Microphone access denied
          </p>
          <p
            css={css`
              color: ${darkTheme.textSecondary};
              font-size: 14px;
              margin: 0;
            `}
          >
            Please allow microphone access in your browser settings to use rehearsal mode.
          </p>
          <div
            css={css`
              display: flex;
              gap: ${spacing.md};
            `}
          >
            <Button shape="rect" size="sm" onClick={requestMic}>
              Try Again
            </Button>
            <Button variant="secondary" shape="rect" size="sm" onClick={onDenied}>
              Cancel
            </Button>
          </div>
        </>
      ) : (
        <p
          css={css`
            color: ${darkTheme.text};
            font-size: 18px;
            margin: 0;
          `}
        >
          Requesting microphone access...
        </p>
      )}
    </div>
  );
}
